import type {
  AlertDryRun,
  AlertInbox,
  AlertRule,
  AlertRuleInput,
  AlertRulePatch,
  Automation,
  AutomationDryRun,
  AutomationParams,
  AutomationTemplate,
  SaveAutomation,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

/** Alerts, alert rules and automation templates (task 70). */

const key = (t?: string) => ['alerts', t] as const;

export function useAlerts(enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id), 'inbox'],
    queryFn: () => api<AlertInbox>('GET', '/alerts'),
    enabled: !!tenant && enabled,
    refetchInterval: 60_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (ids?: string[]) => api<{ unread: number }>('POST', '/alerts/read', ids ? { ids } : {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...key(tenant?.id), 'inbox'] }),
  });
}

export function useAlertRules(enabled: boolean) {
  const { tenant } = useSession();
  return useQuery({ queryKey: [...key(tenant?.id), 'rules'], queryFn: () => api<AlertRule[]>('GET', '/alerts/rules'), enabled: !!tenant && enabled }); // prettier-ignore
}

export function useAlertRuleChanges() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = () => qc.invalidateQueries({ queryKey: [...key(tenant?.id), 'rules'] });
  return {
    create: useMutation({ mutationFn: (body: AlertRuleInput) => api<AlertRule>('POST', '/alerts/rules', body), onSuccess: done }), // prettier-ignore
    update: useMutation({ mutationFn: ({ id, ...body }: AlertRulePatch & { id: string }) => api<AlertRule>('PATCH', `/alerts/rules/${id}`, body), onSuccess: done }), // prettier-ignore
  };
}

export const ruleDryRun = (body: AlertRuleInput) => api<AlertDryRun>('POST', '/alerts/rules/dry-run', body);

export function useAutomations(enabled: boolean) {
  const { tenant } = useSession();
  return useQuery({ queryKey: [...key(tenant?.id), 'automations'], queryFn: () => api<Automation[]>('GET', '/automations'), enabled: !!tenant && enabled }); // prettier-ignore
}

export function useUnderwriters(enabled: boolean) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id), 'underwriters'],
    queryFn: () =>
      api<{ user_id: string; name: string; email: string | null }[]>('GET', '/automations/underwriters'),
    enabled: !!tenant && enabled,
  });
}

export function useSaveAutomation() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: ({ template, ...body }: SaveAutomation & { template: AutomationTemplate }) =>
      api<Automation[]>('PUT', `/automations/${template}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...key(tenant?.id), 'automations'] }),
  });
}

export const automationDryRun = (params: AutomationParams) =>
  api<AutomationDryRun>('POST', '/automations/dry-run', params);
